import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Banknote, Calculator } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { CalculatorTransparency, transparencyData } from './CalculatorTransparency';

const VirkesvardeKalkylator = () => {
  const { language } = useLanguage();
  const [volym, setVolym] = useState(100);
  const [tradslag, setTradslag] = useState('gran');
  const [customPrice, setCustomPrice] = useState(false);
  const [prisPerM3, setPrisPerM3] = useState(1197);
  const [result, setResult] = useState(null);

  // Current Swedish timber prices (Skogsstyrelsen Q4 2025)
  const defaultPrices = {
    gran: 1197,
    tall: 995,
    bjork: 620,
    ek: 1750,
    bok: 900
  };

  const calculateValue = () => {
    const price = customPrice ? prisPerM3 : defaultPrices[tradslag];
    const totalValue = volym * price;
    
    setResult({
      price: price,
      totalValue: totalValue
    });
  };

  const speciesNames = {
    gran: 'Gran',
    tall: 'Tall',
    bjork: 'Björk',
    ek: 'Ek',
    bok: 'Bok'
  };

  const content = {
    sv: {
      title: 'Virkesvärde kalkylator',
      description: 'Beräkna värdet på ditt virke baserat på volym och aktuella virkespris. Verktyget använder uppdaterade priser från Skogsstyrelsen.',
      volume: 'Volym (m³)',
      treeSpecies: 'Trädslag',
      useCustomPrice: 'Använd eget pris',
      calculate: 'Beräkna värde',
      result: 'Resultat',
      pricePerM3: 'Pris per m³',
      totalValue: 'Totalt virkesvärde'
    },
    de: {
      title: 'Holzwert Rechner',
      description: 'Berechnen Sie den Wert Ihres Holzes basierend auf Volumen und aktuellen Preisen.',
      volume: 'Volumen (m³)',
      treeSpecies: 'Baumart',
      useCustomPrice: 'Eigenen Preis verwenden',
      calculate: 'Wert berechnen',
      result: 'Ergebnis',
      pricePerM3: 'Preis pro m³',
      totalValue: 'Gesamtwert'
    },
    en: {
      title: 'Timber Value Calculator',
      description: 'Calculate the value of your timber based on volume and current market prices.',
      volume: 'Volume (m³)',
      treeSpecies: 'Tree species',
      useCustomPrice: 'Use custom price',
      calculate: 'Calculate value',
      result: 'Result',
      pricePerM3: 'Price per m³',
      totalValue: 'Total timber value'
    },
    se: {
      title: 'Muorraárvvu rehkenastin',
      description: 'Rehkenas muora árvvu voluma ja dálááigge haddiid vuođul.',
      volume: 'Voluma (m³)',
      treeSpecies: 'Muorralágat',
      calculate: 'Rehkenas árvvu',
      result: 'Boađus'
    }
  };

  const c = content[language] || content.sv;
  const transparency = transparencyData.virkesvarde?.[language] || transparencyData.virkesvarde?.sv;

  // Build assumptions from current values
  const assumptions = [
    { label: 'Trädslag', value: speciesNames[tradslag], unit: '' },
    { label: 'Volym', value: volym, unit: 'm³' },
    { label: 'Virkespris', value: customPrice ? prisPerM3 : defaultPrices[tradslag], unit: 'SEK/m³' }
  ];

  return (
    <Card className="w-full" data-testid="virkesvarde-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <Banknote className="h-5 w-5" />
          {c.title}
        </CardTitle>
        <CardDescription>{c.description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="volym">{c.volume}</Label>
            <Input
              id="volym"
              type="number"
              value={volym}
              onChange={(e) => setVolym(Number(e.target.value))}
              min={1}
              data-testid="volym-input"
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="tradslag">{c.treeSpecies}</Label>
            <Select value={tradslag} onValueChange={(v) => { setTradslag(v); setPrisPerM3(defaultPrices[v]); }}>
              <SelectTrigger id="tradslag" data-testid="tradslag-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gran">Gran (1 197 kr/m³)</SelectItem>
                <SelectItem value="tall">Tall (995 kr/m³)</SelectItem>
                <SelectItem value="bjork">Björk (620 kr/m³)</SelectItem>
                <SelectItem value="ek">Ek (1 750 kr/m³)</SelectItem>
                <SelectItem value="bok">Bok (900 kr/m³)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2 sm:col-span-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="customPrice"
                checked={customPrice}
                onChange={(e) => setCustomPrice(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="customPrice" className="text-sm">{c.useCustomPrice || 'Använd eget pris'}</Label>
            </div>
            {customPrice && (
              <Input
                type="number"
                value={prisPerM3}
                onChange={(e) => setPrisPerM3(Number(e.target.value))}
                placeholder="SEK per m³"
                data-testid="custom-price-input"
              />
            )}
          </div>
        </div>

        <Button 
          onClick={calculateValue} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          {c.calculate}
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-2">{c.result}</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{c.pricePerM3}</p>
                <p className="text-lg font-semibold text-forest-700">{result.price.toLocaleString('sv-SE')} kr</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{c.totalValue}</p>
                <p className="text-2xl font-bold text-forest-700">{result.totalValue.toLocaleString('sv-SE')} kr</p>
              </div>
            </div>
          </div>
        )}

        {/* Transparency Section */}
        {result && transparency && (
          <CalculatorTransparency
            assumptions={assumptions}
            howItWorks={transparency.howItWorks}
            sources={transparency.sources}
            didYouKnow={transparency.didYouKnow}
            relatedCalculators={transparency.relatedCalculators}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default VirkesvardeKalkylator;
